import { getCssSelector } from 'css-selector-generator'
import { useCallback, useLayoutEffect, useRef } from 'react'
import { GraphicSelection } from 'sprites-types/api/v2/graphics'

// Helper functions for graphics, so that we don't pollute the UI files too much

/**
 * Creates a GraphicSelection object for a given DOM node
 * @param node - The DOM node to create selection for
 * @returns GraphicSelection object or null if unable to determine selection
 */
export function getSelectionForNode(node: Node): GraphicSelection | null {
  // Determine the element to use for CSS selector
  let targetElement: Element
  let cssSelector: string

  if (node.nodeType === Node.ELEMENT_NODE) {
    // Node is an element, use it directly
    targetElement = node as Element
    cssSelector = getCssSelector(targetElement)

    console.log('cssSelector1', cssSelector)
  } else {
    // Node is not an element (e.g., text node), use parent element
    // cssSelect is only for elements, text nodes, we return the parent, and hope that the text allows the llm
    // to differentiate between sibling elements
    const parentElement = node.parentElement
    if (parentElement) {
      targetElement = parentElement
      cssSelector = getCssSelector(parentElement)
    } else {
      // Fallback if no parent element found
      console.error('No parent element found for node', node)
      return null
    }

    console.log('cssSelector2', cssSelector)
  }

  return {
    cssSelector: cssSelector,
    text: targetElement.outerHTML,
  }
}

export const getSelectionNodeInRect = (
  rect: { top: number; left: number; width: number; height: number },
  iframeDoc: Document
): Node | null => {
  const nodes = getSelectionNodesInRect(rect, iframeDoc)

  if (nodes.length === 0) {
    return null
  }

  return nodes[0]
}

// Function to get which nodes to select in a rectangle
export const getSelectionNodesInRect = (
  rect: { top: number; left: number; width: number; height: number },
  iframeDoc: Document
): Node[] => {
  // For now we only return 1 node, later let's support multiple nodes
  const nodes = getTopMostNodesInRect(rect, iframeDoc)
  return nodes
}

// Function to remove nodes from the DOM
export const removeNodes = (nodes: Node[]) => {
  nodes.forEach((node) => {
    if (node.parentNode) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        ;(node as Element).remove()
      } else {
        node.textContent = ''
      }
    }
  })
}

// Function to add style to prevent selection
export const addStyleToPreventSelection = (doc: Document) => {
  const styleId = 'sprites-prevent-selection-style'

  // Check if the style already exists
  if (doc.getElementById(styleId)) {
    return
  }

  const style = doc.createElement('style')
  style.id = styleId
  style.textContent = `
      ::selection {
          background: transparent !important;
      }
      ::-moz-selection {
          background: transparent !important;
      }
      * {
          user-select: none !important;
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
      }
    `
  doc.head.appendChild(style)
}

/**
 * Polyfill for the upcoming React 19 `useEvent` hook.
 * Returns a stable callback whose implementation is always up to date.
 */
export function useEvent<T extends (...args: any[]) => any>(handler: T): T {
  const handlerRef = useRef(handler)
  useLayoutEffect(() => {
    handlerRef.current = handler
  })
  // Stable wrapper

  return useCallback(((...args: any[]) => handlerRef.current(...args)) as T, [])
}

// Returns the bounding box of the node for text editing purposes
export const getEditingBoundingBox = (
  node: Node,
  doc: Document
): { top: number; left: number; width: number; height: number } | null => {
  let rects: DOMRectList

  if (node.nodeType === Node.TEXT_NODE) {
    const range = doc.createRange()
    range.selectNodeContents(node)
    rects = range.getClientRects()
  } else {
    rects = (node as Element).getClientRects()
  }

  if (rects.length === 0) return null

  let minTop = Infinity
  let minLeft = Infinity
  let maxRight = -Infinity
  let maxBottom = -Infinity

  Array.from(rects).forEach((rect) => {
    const rectTop = rect.top
    const rectLeft = rect.left
    const rectRight = rectLeft + rect.width
    const rectBottom = rectTop + rect.height

    minTop = Math.min(minTop, rectTop)
    minLeft = Math.min(minLeft, rectLeft)
    maxRight = Math.max(maxRight, rectRight)
    maxBottom = Math.max(maxBottom, rectBottom)
  })

  // Add 5px padding on all sides
  const paddingVertical = 5
  const paddingHorizontal = 20
  return {
    top: minTop - paddingVertical,
    left: minLeft - paddingHorizontal,
    width: maxRight - minLeft + paddingHorizontal * 2,
    height: maxBottom - minTop + paddingVertical * 2,
  }
}

// Function to get text content from a node
export const getTextContent = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || ''
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    // Check if element contains only text (no child elements)
    const element = node as Element
    const hasOnlyText = Array.from(element.childNodes).every(
      (child) => child.nodeType === Node.TEXT_NODE
    )
    if (hasOnlyText) {
      return element.textContent || ''
    }
  }
  return ''
}

export const getClientRects = (node: Node, doc: Document): DOMRectList => {
  if (node.nodeType === Node.TEXT_NODE) {
    const range = doc.createRange()
    range.selectNodeContents(node)
    return range.getClientRects()
  } else {
    return (node as Element).getClientRects()
  }
}

export const isEditableTextNode = (node: Node): boolean => {
  if (node.nodeType === Node.TEXT_NODE) {
    return true
  } else if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as Element

    if (element.childNodes.length == 0) {
      return false
    }

    const hasOnlyText = Array.from(element.childNodes).every(
      (child) => child.nodeType === Node.TEXT_NODE
    )
    return hasOnlyText
  }
  return false
}

// Get the topmost visible nodes in a rectangle by sampling points
export const getTopMostNodesInRect = (
  rect: { top: number; left: number; width: number; height: number },
  iframeDoc: Document,
  sampleDensity: number = 10
): Node[] => {
  const topMostNodes = new Set<Node>()

  // Calculate sample points
  const samplesX = Math.max(2, Math.floor((rect.width / 100) * sampleDensity))
  const samplesY = Math.max(2, Math.floor((rect.height / 100) * sampleDensity))

  for (let i = 0; i < samplesX; i++) {
    for (let j = 0; j < samplesY; j++) {
      const x = rect.left + (rect.width * i) / (samplesX - 1)
      const y = rect.top + (rect.height * j) / (samplesY - 1)

      // Method 1: Try to get text node at point using caret position
      const textNode = getTextNodeAtPoint(x, y, iframeDoc)
      if (textNode) {
        topMostNodes.add(textNode)
        continue
      }

      // Method 2: Fall back to topmost element
      const elementsAtPoint = iframeDoc.elementsFromPoint(x, y)
      if (elementsAtPoint.length > 0) {
        topMostNodes.add(elementsAtPoint[0])
      }
    }
  }

  return Array.from(topMostNodes)
}

// Helper function to get text node at a specific point
function getTextNodeAtPoint(x: number, y: number, doc: Document): Node | null {
  // First, check what element is actually at this point
  const elementAtPoint = doc.elementFromPoint(x, y)
  if (!elementAtPoint) {
    return null
  }

  // Try modern approach first
  if (doc.caretPositionFromPoint) {
    const caretPos = doc.caretPositionFromPoint(x, y)
    if (caretPos && caretPos.offsetNode?.nodeType === Node.TEXT_NODE) {
      // Verify the text node is actually within the element at the point
      if (elementAtPoint.contains(caretPos.offsetNode)) {
        // Additional check: ensure the text node has visible content at this position
        const range = doc.createRange()
        range.selectNodeContents(caretPos.offsetNode)
        const rects = range.getClientRects()

        // Check if any of the text node's rects contain our point
        for (let i = 0; i < rects.length; i++) {
          const rect = rects[i]
          if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
            return caretPos.offsetNode
          }
        }
      }
    }
  }

  // Try legacy approach for WebKit
  if (doc.caretRangeFromPoint) {
    const range = doc.caretRangeFromPoint(x, y)
    if (range && range.startContainer?.nodeType === Node.TEXT_NODE) {
      // Verify the text node is actually within the element at the point
      if (elementAtPoint.contains(range.startContainer)) {
        // Additional check: ensure the text node has visible content at this position
        const textRange = doc.createRange()
        textRange.selectNodeContents(range.startContainer)
        const rects = textRange.getClientRects()

        // Check if any of the text node's rects contain our point
        for (let i = 0; i < rects.length; i++) {
          const rect = rects[i]
          if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
            return range.startContainer
          }
        }
      }
    }
  }

  return null
}
